import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useCookieConsent } from "../context/CookieConsentContext";
import { useTawkToUi } from "../context/TawkToContext";
import { isPublicTawkPath } from "../navigateAfterAuth";
import {
  getTawkConfig,
  injectTawkScript,
  isTawkScriptInjected,
  removeTawkScript,
  runWhenTawkReady,
} from "../lib/tawkToLoader";

function mapUserRole(role) {
  if (role === "applicant") return "applicant";
  if (role === "hiring-manager" || role === "recruiter") return "hiring";
  return String(role || "").trim();
}

/**
 * tawk.to live chat on public marketing/legal pages only — not in-session product routes.
 * The embed script loads only after support/chat cookie consent.
 */
export default function TawkToWidget({ currentUser }) {
  const { consent } = useCookieConsent();
  const { propertyId, widgetId, enabled } = getTawkConfig();
  const supportAllowed = enabled && Boolean(consent?.support);
  const location = useLocation();
  const showOnThisRoute = isPublicTawkPath(location.pathname);
  const tawkUi = useTawkToUi();
  const suppressCount = tawkUi?.suppressCount ?? 0;
  const shouldHideWidget = suppressCount > 0 || !showOnThisRoute;
  const lastAttributesKeyRef = useRef("");

  useEffect(() => {
    if (!supportAllowed) {
      if (isTawkScriptInjected()) removeTawkScript();
      return undefined;
    }
    injectTawkScript(propertyId, widgetId);
    return undefined;
  }, [supportAllowed, propertyId, widgetId]);

  useEffect(() => {
    if (!supportAllowed) return undefined;

    const email = String(currentUser?.email || "").trim();
    if (!email) {
      lastAttributesKeyRef.current = "";
      return undefined;
    }

    const name = String(currentUser?.name || "").trim() || email;
    const role = mapUserRole(currentUser?.role);
    const attributesKey = `${email}|${name}|${role}`;
    if (attributesKey === lastAttributesKeyRef.current) return undefined;

    runWhenTawkReady(() => {
      if (!window.Tawk_API?.setAttributes) return;
      window.Tawk_API.setAttributes(
        {
          name,
          email,
          role,
        },
        () => {
          lastAttributesKeyRef.current = attributesKey;
        }
      );
    });

    return undefined;
  }, [supportAllowed, currentUser]);

  useEffect(() => {
    if (!supportAllowed) return undefined;

    runWhenTawkReady(() => {
      if (shouldHideWidget) window.Tawk_API.hideWidget?.();
      else window.Tawk_API.showWidget?.();
    });

    return undefined;
  }, [supportAllowed, shouldHideWidget]);

  return null;
}
